import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Alert, Button, Chip, Container, Divider, Spacer, Typography } from '@emburse/embark-core';
import GLDDataTable from '../../../../views/QuickSetup/components/GLDDataTable/GLDDataTable';
import UploadDownload from '../../../../views/QuickSetup/components/UploadDownload';
import { $t, useTranslation } from '../../../../hooks/useTranslation';
import { generateRandomGUID } from '../../../../util/helpers';
import { defaultValidation } from '../../../../util/formValidation';
const EmployeeDimensions = forwardRef(({ data, loading, upload, download, alert }, ref) => {
    useTranslation();
    const [tableData, setTableData] = useState([]);
    useEffect(() => {
        if (data && data.length) {
            let updatedData = JSON.parse(JSON.stringify(data));
            updatedData = updatedData.map((item) => ({
                ...item,
                Rows: item.Rows.map((row) => ({ ...row, id: generateRandomGUID() }))
            }));
            setTableData(updatedData);
        }
        else {
            setTableData([]);
        }
    }, [data]);
    const rowValidation = (row) => {
        const nameInvalid = defaultValidation(row.Name);
        const name = nameInvalid.length && { field: 'Name', reason: nameInvalid };
        return [name];
    };
    useImperativeHandle(ref, () => ({
        getData() {
            return JSON.stringify(tableData);
        },
        isValid() {
            const mappedTable = tableData.map((item) => {
                const filteredValidation = item.Rows.map((row) => rowValidation(row).filter((cell) => !!cell));
                const invalid = filteredValidation.filter((valid) => valid.length > 0);
                return (invalid.length > 0 && {
                    table: item.Label,
                    cell: filteredValidation.flat()
                });
            });
            return mappedTable.filter((item) => !!item);
        }
    }));
    const updateTableData = (rows, index) => {
        const tableDataCopy = JSON.parse(JSON.stringify(tableData));
        tableDataCopy[index].Rows = rows;
        setTableData(tableDataCopy);
    };
    const updateLabel = (label, index) => {
        const tableDataCopy = JSON.parse(JSON.stringify(tableData));
        tableDataCopy[index].Label = label;
        setTableData(tableDataCopy);
    };
    return (_jsxs(Container, { disableGutters: true, children: [_jsx(Typography, { variant: "h4", children: $t('Employee General Ledger Dimensions') }), _jsx(Spacer, { size: 10 }), _jsx(Chip, { label: $t('Optional Information'), variant: "outlined", color: "primary", size: "small" }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t(`Employee General Ledger Dimensions are attributes specific to a user and assigned by a 
                Certify administrator, and cannot be edited by the user.`) }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t(`For example, setting up an Employee GLD labeled 'Sales Region' and assigning users values of 
                'North America', 'Latin America', 'Asia-Pacific', and 'Europe/Middle East/Africa' will allow you to understand 
                how much sales representatives assigned to each region are spending.`) }), _jsx(Spacer, { size: 10 }), _jsx(Typography, { children: $t('Use this page to set up your Employee GLDs, and the subsequent page to assign users to them.') }), _jsx(Spacer, { size: 35 }), _jsx(Divider, { orientation: "horizontal", variant: "fullWidth", style: { width: '100%' }, light: true }), alert.show && (_jsxs(_Fragment, { children: [_jsx(Spacer, { size: 15 }), _jsx(Alert, { alertTitle: alert.title, severity: alert.severity, action: _jsx(Button, { color: alert.severity, onClick: alert.onClick, children: $t('See All Errors') }), children: alert.text })] })), _jsx(Spacer, { size: 35 }), _jsx(UploadDownload, { upload: upload, download: download }), _jsx(Spacer, { size: 15 }), tableData.map((GLD, index) => (_jsxs(Fragment, { children: [_jsx(GLDDataTable, { tableData: GLD.Rows, label: GLD.Label, loading: loading, index: index, updateTableData: (rows) => updateTableData(rows, index), updateLabel: (label) => updateLabel(label, index) }), _jsx(Spacer, { size: 35 })] }, GLD.id)))] }));
});
export default EmployeeDimensions;
